<template>
  <v-layout no-gutters>
    <v-flex class="auction-form" md9 lg7>
      <div class="page-title">{{ pageTitleText }}</div>
      <v-form @submit.prevent="createAuction()" class="auction-form-background" ref="form">
        <v-layout column class="form-block" no-gutters>
          <v-flex xs12 sm12 md12 class="form-field">
            <input
              class="custom-input"
              id="title"
              v-on:keydown.enter.prevent=""
              v-on:blur="items.length === 0 && title !== '' ? bindTitle() : null"
              v-model.trim="$v.title.$model"
              placeholder="Title *"
            />
            <span v-if="!$v.title.required && showErrors" class="error-input"
              >This field is required</span
            >
          </v-flex>
          <v-flex xs12 sm12 md12>
            <v-textarea
              style="max-height: 50vh; height: auto; overflow: auto"
              class="custom-input textarea"
              id="description"
              name="description"
              placeholder="Description"
              v-model="description"
              rows="5"
              auto-grow
            ></v-textarea>
          </v-flex>
          <v-flex xs12 sm12 mb-4 class="form-field">
            <v-select
              solo
              class="auc-type"
              v-model="$v.choosedAuctionType.$model"
              :items="auctionType"
              item-text="text"
              hide-details
              item-value="value"
              id="auc-type"
              filled
              label="Auction Type *"
            ></v-select>
            <span v-if="!$v.choosedAuctionType.required && showErrors" class="error-input mt-2"
              >This field is required</span
            >
          </v-flex>
          <v-flex xs12 sm12 md12>
            <v-layout row justify-space-between no-gutters>
              <v-flex sm12 md5 class="pr-md-6" v-if="choosedAuctionType !== MULTILOT_TYPE">
                <v-layout
                  no-gutters
                  class="custom-group form-field full-width-field"
                  column
                  justify-center
                  align-start
                >
                  <div class="label-with-tooltip">
                    <span class="placeholder">Starting price *</span>
                    <AuctionFormInfoTooltip
                      aria-label="Starting price info"
                      text="The maximum accepted price for this auction. Bidders must place bids below this value."
                    />
                  </div>
                  <input
                    class="custom-input"
                    ref="value"
                    type="number"
                    v-if="$route.name === 'auctionEdit' && value"
                    v-model="$v.value.$model"
                    v-on:keydown.enter.prevent=""
                    placeholder="0"
                  />
                  <input
                    class="custom-input"
                    ref="value"
                    id="value"
                    type="number"
                    v-else
                    v-model="$v.value.$model"
                    v-on:keydown.enter.prevent=""
                    placeholder="0"
                  />
                  <span v-if="!$v.value.required && showErrors" class="error-input"
                    >This field is required</span
                  >
                </v-layout>
              </v-flex>
              <v-flex
                sm12
                md4
                v-if="choosedAuctionType !== INSIDER_TYPE && choosedAuctionType !== MULTILOT_TYPE"
                class="pr-md-6"
              >
                <v-layout
                  no-gutters
                  class="custom-group form-field"
                  column
                  justify-center
                  align-start
                >
                  <div class="label-with-tooltip">
                    <span class="placeholder">Minimum bid step *</span>
                    <AuctionFormInfoTooltip
                      aria-label="Minimum bid step info"
                      text="The minimum amount by which each new bid must be lower than the current lowest bid."
                    />
                  </div>
                  <input
                    class="custom-input"
                    ref="minimalStep"
                    v-on:keydown.enter.prevent=""
                    v-if="$route.name === 'auctionEdit' && minimalStep"
                    v-model="$v.minimalStep.$model"
                    type="number"
                    placeholder="0"
                  />
                  <input
                    class="custom-input"
                    ref="minimalStep"
                    type="number"
                    v-on:keydown.enter.prevent=""
                    id="minimal-step"
                    v-else
                    v-model="$v.minimalStep.$model"
                    placeholder="0"
                  />
                  <span v-if="!$v.minimalStep.required && showErrors" class="error-input"
                    >This field is required</span
                  >
                </v-layout>
              </v-flex>
              <v-flex sm12 md9 class="pr-md-6" v-if="choosedAuctionType === MULTILOT_TYPE">
                <v-layout
                  no-gutters
                  class="custom-group form-field full-width-field"
                  column
                  justify-center
                  align-start
                >
                  <span class="placeholder">Total starting price</span>
                  <div class="multilot-total-readout">
                    {{ formatDisplayNumber(multilotTotalValue) }} {{ currency }}
                  </div>
                  <span class="multilot-total-hint">
                    Derived from the sum of each lot's starting value below.
                  </span>
                </v-layout>
              </v-flex>
              <v-flex sm12 md3>
                <v-layout
                  no-gutters
                  class="custom-group full-width-field form-field"
                  column
                  justify-center
                  align-start
                >
                  <span class="placeholder">Currency</span>
                  <v-autocomplete
                    v-model="currency"
                    id="currency"
                    single-line
                    :placeholder="defaultCurrencyCode"
                    no-data-text="There is no such currency"
                    menu-props="auto"
                    class="custom-input currencies mt-0"
                    :rules="rules['requiredValue']"
                    outline
                    :items="currencyChoices"
                  ></v-autocomplete>
                </v-layout>
              </v-flex>
            </v-layout>
          </v-flex>

          <AuctionFormLots
            v-if="choosedAuctionType === MULTILOT_TYPE"
            :lots.sync="lots"
            :currency="currency"
            :show-errors="showErrors"
          />

          <AuctionFormInsiderDutch
            v-if="choosedAuctionType === INSIDER_TYPE"
            :value="value"
            :currency="currency"
            :dutch-steps.sync="dutchSteps"
            :time-of-one-step.sync="timeOfOneStep"
            :err-dutch-required="!$v.dutchSteps.required && showErrors"
            :err-dutch-min="!$v.dutchSteps.minValue && showErrors"
            :err-dutch-max="!$v.dutchSteps.maxValue && showErrors"
            :err-time-required="!$v.timeOfOneStep.required && showErrors"
            :err-time-min="!$v.timeOfOneStep.minValue && showErrors"
            :err-time-max="!$v.timeOfOneStep.maxValue && showErrors"
            @dutch-steps-in="dutchStepsIn"
            @dutch-steps-out="dutchStepsOut"
            @time-step-in="dutchTimeOfStepIn"
            @time-step-out="dutchTimeOfStepOut"
          />

          <AuctionFormSchedule
            :is-english-reverse-continuous-type="requiresEndSchedule"
            :reverse-continuous-specific-min-gap-minutes="reverseContinuousSpecificMinGapMinutes"
            :start-schedule-value.sync="startScheduleValue"
            :end-schedule-value.sync="endScheduleValue"
            :show-errors="showErrors"
            :start-error-key="scheduleValidation.startErrorKey"
            :end-error-key="scheduleValidation.endErrorKey"
            :tender-period-end-date-iso="tenderPeriodEndDateIso"
            :auction-start-date-menu-open.sync="auctionStartDateMenuOpen"
            :auction-start-date-iso.sync="auctionStartDateIso"
            :auction-start-date-display.sync="auctionStartDateDisplay"
            :auction-start-time-menu-open.sync="auctionStartTimeMenuOpen"
            :auction-start-time.sync="auctionStartTime"
            :auction-end-date-menu-open.sync="auctionEndDateMenuOpen"
            :auction-end-date-iso.sync="auctionEndDateIso"
            :auction-end-date-display.sync="auctionEndDateDisplay"
            :auction-end-time-menu-open.sync="auctionEndTimeMenuOpen"
            :auction-end-time.sync="auctionEndTime"
            :auction-end-date-picker-min="auctionEndDatePickerMin"
            :auction-end-time-picker-min="auctionEndTimePickerMin"
            @request-show-errors="onScheduleRequestShowErrors"
          />

          <AuctionFormReverseAntiSnipe
            v-if="choosedAuctionType === ENGLISH_REVERSE_CONTINUOUS_TYPE"
            :enabled.sync="antiSnipingEnabled"
            :extension-period-minutes.sync="extensionPeriodMinutes"
            :err-extension="
              antiSnipingEnabled && !$v.extensionPeriodMinutes.nonNegativeOptionalMinutes && showErrors
            "
          />

          <AuctionFormReservePrice
            v-if="choosedAuctionType === ENGLISH_REVERSE_CONTINUOUS_TYPE"
            :enabled.sync="reservePriceEnabled"
            :reserve-price.sync="reservePrice"
            :err-reserve-price="
              reservePriceEnabled && !$v.reservePrice.positiveWhenReserveEnabled && showErrors
            "
          />

          <v-flex
            v-if="choosedAuctionType === ENGLISH_REVERSE_CONTINUOUS_TYPE"
            xs12
            class="proxy-bidding-toggle form-section-gap"
          >
            <div class="proxy-bidding-toggle-inner">
              <input
                id="enable-proxy-bidding"
                type="checkbox"
                v-model="proxyBiddingEnabled"
              />
              <label for="enable-proxy-bidding" class="placeholder mb-0">Proxy bids enabled</label>
            </div>
          </v-flex>

          <v-flex
            v-if="requiresEndSchedule && auctionDurationSummaryText"
            xs12
            class="auction-duration-text"
          >
            {{ auctionDurationSummaryText }}
          </v-flex>

          <div class="form-section-gap">
            <v-toolbar class="elevation-1" text color="white">
              <v-toolbar-title>Items</v-toolbar-title>
              <v-divider class="mx-2" inset vertical></v-divider>
              <v-spacer></v-spacer>
              <v-dialog v-model="dialog" max-width="800px">
                <template v-slot:activator="{ on }">
                  <v-btn
                    @click="resetValidate"
                    color="primary"
                    dark
                    class="mb-2 new-item-button"
                    v-on="on"
                    >New Item</v-btn
                  >
                </template>
                <v-card ref="form">
                  <v-card-title>
                    <span class="headline">{{ formTitle }}</span>
                  </v-card-title>

                  <v-card-text>
                    <v-container id="items-dialog" grid-list-md>
                      <v-form>
                        <v-layout wrap>
                          <v-flex xs12 sm6 md8>
                            <v-text-field
                              ref="description"
                              v-model="$v.editedItem.description.$model"
                              label="Title"
                            ></v-text-field>
                            <span
                              v-if="!$v.editedItem.description.required && errorItems"
                              class="error-input"
                              >This field is required</span
                            >
                          </v-flex>
                          <v-flex xs12 sm6 md2>
                            <v-text-field
                              required
                              @change="checkQuantity"
                              type="number"
                              v-model="quantity"
                              label="Quantity"
                            ></v-text-field>
                          </v-flex>
                          <v-flex xs12 sm6 md2>
                            <v-text-field
                              v-model="editedItem.unit.name"
                              label="Unit"
                            ></v-text-field>
                          </v-flex>
                          <v-flex xs12 sm6 md4 v-if="choosedAuctionType === MULTILOT_TYPE">
                            <v-select
                              v-model="$v.editedItem.relatedLot.$model"
                              :items="lotSelectOptions"
                              item-text="title"
                              item-value="id"
                              label="Lot *"
                            ></v-select>
                            <span
                              v-if="!$v.editedItem.relatedLot.required && errorItems"
                              class="error-input"
                              >This field is required</span
                            >
                          </v-flex>
                          <v-flex xs12 sm6 md12>
                            <v-text-field
                              ref="documentUrl"
                              v-model="$v.editedItem.documentUrl.$model"
                              label="Document Url"
                            ></v-text-field>
                            <span
                              v-if="!$v.editedItem.documentUrl.url && errorItems"
                              class="error-input"
                              >Invalid Url</span
                            >
                          </v-flex>
                        </v-layout>
                      </v-form>
                    </v-container>
                  </v-card-text>

                  <v-card-actions>
                    <v-spacer></v-spacer>
                    <v-btn color="blue darken-1" text @click="close">Cancel</v-btn>
                    <v-btn color="blue darken-1" text @click="save">Save</v-btn>
                  </v-card-actions>
                </v-card>
              </v-dialog>
            </v-toolbar>
            <v-data-table
              :headers="tableHeaders"
              :items="items"
              :no-data-text="AUCTION_FORM_ITEMS_TABLE_NO_DATA"
              class="elevation-1 items-table"
            >
              <template v-slot:item.actions="{ item }">
                <td class="justify-center layout px-0">
                  <v-icon small class="mr-2" @click="editItem(item)"> edit </v-icon>
                  <v-icon small @click="deleteItem(item)"> delete </v-icon>
                </td>
              </template>

              <template v-slot:item.quantity="{ item }">
                {{ formatDisplayNumber(item.quantity) }}<span v-if="item.unit && item.unit.name"> {{ item.unit.name }}</span>
              </template>

              <template v-slot:item.documentUrl="{ item }">
                <td class="text-xs-left">{{ cutString(item.documentUrl, 30) }}</td>
              </template>

              <template v-slot:item.relatedLot="{ item }">
                <td class="text-xs-left">{{ lotTitleById[item.relatedLot] || '' }}</td>
              </template>
            </v-data-table>
          </div>

          <v-layout row justify-center no-gutters>
            <span v-if="showErrors && !items.length" class="error-input t-a form-items-error">
              Items are required!
            </span>
            <span
              v-if="showErrors && isMultilotType && items.length && !itemsHaveLotsAssigned"
              class="error-input t-a form-items-error"
            >
              Every item must be assigned to a lot!
            </span>
          </v-layout>
          <v-layout row>
            <v-flex xs12 sm12 md12>
              <button
                :class="[
                  'submit-button',
                  { 'submit-button-create': $route.name !== 'auctionEdit' },
                ]"
                :id="$route.name !== 'auctionEdit' ? 'submit-auc-creation' : 'submit-auc-edit'"
                :disabled="isDisabledSubmit"
                type="submit"
                :loading="isDisabledSubmit"
              >
                {{ $route.name === 'auctionEdit' ? 'Edit' : 'Create' }}
              </button>
            </v-flex>
          </v-layout>
        </v-layout>
      </v-form>
    </v-flex>
  </v-layout>
</template>
<script>
import Vue from 'vue';
import { required, requiredIf, minValue, maxValue, url } from 'vuelidate/lib/validators';
import utils from '@/utils/utils';
import { formatDisplayNumber } from '@/utils/numberFormat';
import currencies from '@/assets/currencies.json';
import _ from 'lodash';
import {
  AUCTION_TYPE_OPTIONS,
  INSIDER_TYPE,
  ENGLISH_REVERSE_CONTINUOUS_TYPE,
  MULTILOT_TYPE,
} from '@/constants/auction';
import AuctionFormInsiderDutch from '@/components/forms/auctionForm/AuctionFormInsiderDutch.vue';
import AuctionFormInfoTooltip from '@/components/forms/auctionForm/AuctionFormInfoTooltip.vue';
import AuctionFormReverseAntiSnipe from '@/components/forms/auctionForm/AuctionFormReverseAntiSnipe.vue';
import AuctionFormReservePrice from '@/components/forms/auctionForm/AuctionFormReservePrice.vue';
import AuctionFormSchedule from '@/components/forms/auctionForm/AuctionFormSchedule.vue';
import AuctionFormLots from '@/components/forms/auctionForm/AuctionFormLots.vue';
import {
  AUCTION_FORM_ITEMS_TABLE_NO_DATA,
  REVERSE_CONTINUOUS_SPECIFIC_MIN_GAP_MINUTES,
  SCHEDULE_SPECIFIC,
} from '@/components/forms/auctionForm/auctionFormConstants';
import {
  formatDate,
  parseIsoAndTimeToMoment,
  buildScheduleStartMoment,
} from '@/components/forms/auctionForm/dateUtils';
import { validateSchedule } from '@/components/forms/auctionForm/scheduleValidation';
import {
  mapAuctionItems,
  buildAuctionPeriod,
  addTypeSpecificPayload,
  buildMultilotLots,
  generateLotId,
} from '@/components/forms/auctionForm/auctionFormPayload';
import { mapActions } from 'vuex';
const requiredIfRoot = requiredIf(function () {
  return this.isRequiredMinimalStep;
});
const requiredDutch = requiredIf(function () {
  return this.requiredDutchStepsAndPeriod;
});
const requiredIfMultilot = requiredIf(function () {
  return this.choosedAuctionType === MULTILOT_TYPE;
});
function nonNegativeOptionalMinutes(value) {
  if (value === '' || value === null || value === undefined) return true;
  if (typeof value === 'number' && Number.isNaN(value)) return true;
  const n = Number(value);
  return Number.isFinite(n) && n >= 0;
}
export default {
  name: 'auctionCreate',
  data() {
    return {
      AUCTION_FORM_ITEMS_TABLE_NO_DATA,
      errorItems: false,
      media: 0,
      alarm: 0,
      dutchSteps: 80,
      timeOfOneStep: 1,
      valid: false,
      diffTenderMinutes: 11,
      headers: [
        {
          text: 'Description',
          align: 'left',
          sortable: true,
          value: 'description',
        },
        {
          text: 'Quantity',
          align: 'left',
          sortable: true,
          value: 'quantity',
        },
        {
          text: 'Document',
          align: 'left',
          sortable: false,
          value: 'documentUrl',
        },
        { text: '', value: 'actions', sortable: false },
      ],
      rules: {
        required: (value) => !!value || 'This field is required',
      },
      editedIndex: -1,
      editedItem: {
        description: '',
        quantity: 1,
        documentUrl: '',
        unit: {
          name: '',
        },
        relatedLot: '',
      },
      defaultItem: {
        description: '',
        quantity: 1,
        documentUrl: '',
        unit: {
          name: '',
        },
        relatedLot: '',
      },
      dialog: false,
      startScheduleValue: null,
      endScheduleValue: null,
      items: [],
      itemsTemp: [
        {
          description: '',
          quantity: '1',
          unit: {
            name: '',
          },
          documentUrl: '',
          relatedLot: '',
        },
      ],
      lots: [{ id: generateLotId(), title: '', value: null, minimalStep: null }],
      errorDocumentsUrl: false,
      itemsErrorsQuantity: false,
      itemsErrorsDescription: false,
      tenderPeriodEndDateIso: null,
      auctionStartDateIso: null,
      tenderPeriodEndDateDisplay: null,
      auctionStartDateDisplay: null,
      auctionStartDateMenuOpen: false,
      tenderEndTime: '',
      auctionStartTime: '',
      auctionEndDateIso: null,
      auctionEndDateDisplay: null,
      auctionEndDateMenuOpen: false,
      auctionEndTimeMenuOpen: false,
      auctionEndTime: '',
      antiSnipingEnabled: false,
      extensionPeriodMinutes: 5,
      reservePriceEnabled: false,
      reservePrice: 0,
      proxyBiddingEnabled: false,
      showErrors: false,
      auctionStartTimeMenuOpen: false,
      auctionFile: {
        imageName: '',
        imageUrl: '',
        imageFile: '',
      },
      removeAuctionFile: false,
      auctionLogoServer: '',
      title: '',
      description: '',
      defaultCurrencyCode: utils.DEFAULT_CURRENCY_CODE,
      currency: utils.DEFAULT_CURRENCY_CODE,
      valueAddedTaxIncluded: false,
      auctionType: AUCTION_TYPE_OPTIONS,
      INSIDER_TYPE,
      ENGLISH_REVERSE_CONTINUOUS_TYPE,
      MULTILOT_TYPE,
      reverseContinuousSpecificMinGapMinutes: REVERSE_CONTINUOUS_SPECIFIC_MIN_GAP_MINUTES,
      choosedAuctionType: '',
      currencyChoices: [],
      itemCount: 1,
      activeItems: 1,
      value: 10000,
      minimalStep: 100,
      itemModels: { 1: {} },
      scheduleValidationTick: 0,
    };
  },
  props: {
    isDisabledSubmit: {
      required: true,
      type: Boolean,
    },
  },
  created() {
    this.currencyChoices = utils.sortCurrencyCodeList(Object.keys(currencies[0]));
  },
  computed: {
    pageTitleText() {
      return this.$route.name === 'auctionEdit' ? 'Edit Auction' : 'Create Auction';
    },
    form() {
      return {
        description: this.editedItem.description,
        documentUrl: this.editedItem.documentUrl,
      };
    },
    formTitle() {
      return this.editedIndex === -1 ? 'New Item' : 'Edit Item';
    },
    requiredDutchStepsAndPeriod() {
      return this.choosedAuctionType === INSIDER_TYPE;
    },
    isEnglishReverseContinuousType() {
      return this.choosedAuctionType === ENGLISH_REVERSE_CONTINUOUS_TYPE;
    },
    isMultilotType() {
      return this.choosedAuctionType === MULTILOT_TYPE;
    },
    /** Both continuous and multilot auctions need the end-of-schedule picker (multilot needs it for the shared per-lot plannedEndDate). */
    requiresEndSchedule() {
      return this.isEnglishReverseContinuousType || this.isMultilotType;
    },
    isRequiredMinimalStep() {
      return this.choosedAuctionType !== INSIDER_TYPE;
    },
    multilotTotalValue() {
      return this.lots.reduce((sum, lot) => sum + (Number(lot.value) || 0), 0);
    },
    lotsAreValid() {
      if (!this.isMultilotType) return true;
      return (
        this.lots.length > 0 &&
        this.lots.every((lot) => {
          const hasTitle = Boolean(lot.title && String(lot.title).trim().length);
          const hasPositiveValue = Number(lot.value) > 0;
          const step = lot.minimalStep;
          const hasValidStep = step !== null && step !== '' && Number(step) >= 0;
          return hasTitle && hasPositiveValue && hasValidStep;
        })
      );
    },
    itemsHaveLotsAssigned() {
      if (!this.isMultilotType) return true;
      return this.items.length > 0 && this.items.every((item) => Boolean(item.relatedLot));
    },
    lotSelectOptions() {
      return this.lots.map((lot) => ({ id: lot.id, title: lot.title || '(untitled lot)' }));
    },
    lotTitleById() {
      const map = {};
      this.lots.forEach((lot) => {
        map[lot.id] = lot.title;
      });
      return map;
    },
    tableHeaders() {
      if (!this.isMultilotType) return this.headers;
      const withoutActions = this.headers.filter((h) => h.value !== 'actions');
      const actionsHeader = this.headers.find((h) => h.value === 'actions');
      const lotHeader = { text: 'Lot', align: 'left', sortable: false, value: 'relatedLot' };
      return actionsHeader
        ? [...withoutActions, lotHeader, actionsHeader]
        : [...withoutActions, lotHeader];
    },
    isCreateFormInvalid() {
      return (
        this.$v.choosedAuctionType.$invalid ||
        this.$v.title.$invalid ||
        this.$v.value.$invalid ||
        this.$v.minimalStep.$invalid ||
        this.$v.extensionPeriodMinutes.$invalid ||
        this.$v.reservePrice.$invalid ||
        this.$v.dutchSteps.$invalid ||
        this.$v.timeOfOneStep.$invalid
      );
    },
    scheduleValidation() {
      // Force recompute on explicit submit/save actions.
      this.scheduleValidationTick;
      return validateSchedule({
        isEnglishReverseContinuousType: this.requiresEndSchedule,
        reverseContinuousSpecificMinGapMinutes: this.reverseContinuousSpecificMinGapMinutes,
        startScheduleValue: this.startScheduleValue,
        startDateDisplay: this.auctionStartDateDisplay,
        startDateIso: this.auctionStartDateIso,
        startTime: this.auctionStartTime,
        endScheduleValue: this.endScheduleValue,
        endDateDisplay: this.auctionEndDateDisplay,
        endDateIso: this.auctionEndDateIso,
        endTime: this.auctionEndTime,
        nowMoment: Vue.moment(),
      });
    },

    auctionEndDatePickerMin() {
      if (!this.requiresEndSchedule || this.endScheduleValue !== SCHEDULE_SPECIFIC)
        return undefined;
      const start = this.getAuctionStartMoment();
      if (!start || !start.isValid()) return undefined;
      return start
        .clone()
        .add(REVERSE_CONTINUOUS_SPECIFIC_MIN_GAP_MINUTES, 'minutes')
        .format('YYYY-MM-DD');
    },
    auctionEndTimePickerMin() {
      if (!this.requiresEndSchedule || this.endScheduleValue !== SCHEDULE_SPECIFIC)
        return undefined;
      const start = this.getAuctionStartMoment();
      if (!start || !start.isValid() || !this.auctionEndDateIso) return undefined;
      const minEnd = start.clone().add(REVERSE_CONTINUOUS_SPECIFIC_MIN_GAP_MINUTES, 'minutes');
      if (this.auctionEndDateIso !== minEnd.format('YYYY-MM-DD')) return undefined;
      return minEnd.format('HH:mm');
    },
    /** Plain-text summary after start/end schedule (preset minutes or span for specific end). */
    auctionDurationSummaryText() {
      if (!this.requiresEndSchedule) return '';
      const ev = this.endScheduleValue;
      if (typeof ev === 'number' && Number.isFinite(ev) && ev > 0) {
        const unit = ev === 1 ? 'minute' : 'minutes';
        return `Auction duration: ${ev} ${unit}`;
      }
      if (ev !== SCHEDULE_SPECIFIC) return '';
      const start = this.getAuctionStartMoment();
      const end = this.scheduleMomentFromIsoAndTime(this.auctionEndDateIso, this.auctionEndTime);
      if (!start || !start.isValid() || !end || !end.isValid()) return '';
      const minutes = Math.round(end.diff(start, 'minutes', true));
      if (!Number.isFinite(minutes) || minutes < 0) return '';
      const human = this.formatAuctionDurationHuman(minutes);
      return human ? `Auction duration: ${human}` : '';
    },
    quantity: {
      get() {
        return this.editedItem.quantity;
      },
      set(value) {
        this.editedItem.quantity = value;
      },
    },
  },
  methods: {
    ...mapActions(['showSnackbar']),
    dutchStepsOut() {
      this.dutchSteps = this.dutchSteps - 1 || 1;
    },
    dutchStepsIn() {
      this.dutchSteps = this.dutchSteps + 1 || 99;
    },
    dutchTimeOfStepOut() {
      this.timeOfOneStep = this.timeOfOneStep - 0.5 || 0.5;
    },
    dutchTimeOfStepIn() {
      this.timeOfOneStep = this.timeOfOneStep + 0.5 || 5;
    },
    resetValidate() {
      this.errorItems = false;
      this.$nextTick(() => {
        this.$nextTick(() => {
          Object.keys(this.form).forEach((f) => {
            const cmp = this.$refs[f];
            if (cmp && typeof cmp.reset === 'function') {
              cmp.reset();
            }
          });
        });
      });
    },
    calculateDiffMinutes() {
      let today = Vue.moment(Date.parse(Vue.moment())).format('YYYY-MM-DD');
      const timeStr = this.auctionStartTime;
      const timeParts =
        timeStr && typeof timeStr === 'string' && timeStr.includes(':') ? timeStr.split(':') : null;
      if (
        timeParts &&
        timeParts[0] === '00' &&
        timeParts[0] >= 0 &&
        timeParts[1] < 10 &&
        this.auctionStartDateIso !== today
      ) {
        this.tenderPeriodEndDateIso = Vue.moment(
          Date.parse(Vue.moment(this.auctionStartDateIso, 'YYYY/MM/DD').subtract(1, 'days'))
        ).format('YYYY-MM-DD');
      } else {
        this.tenderPeriodEndDateIso = this.auctionStartDateIso;
      }
    },
    editItem(item) {
      this.editedIndex = this.items.indexOf(item);
      this.editedItem = _.cloneDeep(item);
      this.dialog = true;
    },

    deleteItem(item) {
      const index = this.items.indexOf(item);
      confirm('Are you sure you want to delete this item?') && this.items.splice(index, 1);
    },

    close() {
      this.dialog = false;
      setTimeout(() => {
        this.editedItem = _.cloneDeep(this.defaultItem);
        this.editedIndex = -1;
      }, 300);
    },
    save() {
      this.errorItems = true;
      if (!this.$v.editedItem.$invalid) {
        if (this.editedIndex > -1) {
          Object.assign(this.items[this.editedIndex], this.editedItem);
        } else {
          this.items.push(this.editedItem);
        }
        this.close();
      }
    },

    cutString(text, size) {
      if (text && text.length > size) {
        return text.slice(0, size) + '...';
      } else {
        return text;
      }
    },
    checkQuantity() {
      if (this.editedItem.quantity < 0.001) {
        this.editedItem.quantity = 1;
      }

      const normalized = Math.round(Number(this.editedItem.quantity) * 1000) / 1000;
      if (Number.isFinite(normalized)) {
        this.editedItem.quantity = formatDisplayNumber(normalized);
      }
    },
    formatDisplayNumber,
    formatDate(date) {
      return formatDate(date);
    },
    bindTitle() {
      this.itemsTemp[0].description = this.title;
      this.itemsTemp[0].documentUrl = '';
      this.itemsTemp[0].quantity = '1';
      this.itemsTemp[0].unit.name = '';
      this.items = _.cloneDeep(this.itemsTemp);
    },
    formatAuctionDurationHuman(totalMinutes) {
      const n = Math.round(Number(totalMinutes));
      if (!Number.isFinite(n) || n < 0) return '';
      if (n === 0) return '0 minutes';
      let rest = n;
      const weeks = Math.floor(Vue.moment.duration(rest, 'minutes').asWeeks());
      rest -= weeks * 7 * 24 * 60;
      const days = Math.floor(Vue.moment.duration(rest, 'minutes').asDays());
      rest -= days * 24 * 60;
      const tail = Vue.moment.duration(rest, 'minutes');
      const hours = tail.hours();
      const mins = tail.minutes();
      const parts = [];
      const push = (count, singular, plural) => {
        if (count > 0) parts.push(`${count} ${count === 1 ? singular : plural}`);
      };
      push(weeks, 'week', 'weeks');
      push(days, 'day', 'days');
      push(hours, 'hour', 'hours');
      push(mins, 'minute', 'minutes');
      return parts.length ? parts.join(' ') : '0 minutes';
    },
    scheduleMomentFromIsoAndTime(dateIso, timeStr) {
      return parseIsoAndTimeToMoment(dateIso, timeStr);
    },
    getAuctionStartMoment() {
      return buildScheduleStartMoment(
        this.startScheduleValue,
        this.auctionStartDateIso,
        this.auctionStartTime
      );
    },
    refreshScheduleValidationNow() {
      this.scheduleValidationTick += 1;
    },
    onScheduleRequestShowErrors() {
      this.refreshScheduleValidationNow();
      this.showErrors = true;
    },
    createAuction() {
      this.refreshScheduleValidationNow();
      const scheduleValidationNow = this.scheduleValidation;
      this.calculateDiffMinutes();
      const typeAuction = this.choosedAuctionType;
      const isMultilot = typeAuction === MULTILOT_TYPE;
      const hasSpecificStart = this.startScheduleValue === SCHEDULE_SPECIFIC;
      if (
        !scheduleValidationNow.isValid ||
        this.isCreateFormInvalid ||
        (isMultilot && !this.lotsAreValid)
      ) {
        this.showErrors = true;
        return;
      }
      if (this.items[0] && this.items[0].description) {
        if (isMultilot && !this.itemsHaveLotsAssigned) {
          this.showErrors = true;
          return;
        }
        let today = new Date();
        const items = mapAuctionItems(this.items);
        this.showErrors = false;
        const auctionPeriod = buildAuctionPeriod({
          typeAuction,
          hasSpecificStart,
          auctionStartDateIso: this.auctionStartDateIso,
          auctionStartTime: this.auctionStartTime,
          startScheduleValue: this.startScheduleValue,
          endScheduleValue: this.endScheduleValue,
          auctionEndDateIso: this.auctionEndDateIso,
          auctionEndTime: this.auctionEndTime,
        });
        let dataSend = {
          title: this.title,
          procurementMethodType: typeAuction,
          description: this.description,
          items,
          tenderPeriod: {
            endDate: hasSpecificStart
              ? this.tenderPeriodEndDateIso + 'T' + this.tenderEndTime + ':00'
              : Vue.moment(today).tz('Europe/Kiev').format('YYYY-MM-DD') +
                'T' +
                Vue.moment(today.getTime() + (this.startScheduleValue - (1000 * 60 * 10 - 5000)))
                  .tz('Europe/Kiev')
                  .format('HH:mm:ss'),
          },
        };
        if (isMultilot) {
          // Multilot: value/minimalStep are DERIVED totals over the lots, and the
          // schedule lives per-lot (auctionPeriod), not at the tender level.
          const lots = buildMultilotLots({ lots: this.lots, currency: this.currency, auctionPeriod });
          const totalValue = lots.reduce((sum, lot) => sum + Number(lot.value.amount || 0), 0);
          const minStep = lots.reduce(
            (min, lot) => Math.min(min, Number(lot.minimalStep.amount || 0)),
            Number(lots[0].minimalStep.amount || 0)
          );
          dataSend.value = { amount: totalValue, currency: this.currency };
          dataSend.minimalStep = { amount: minStep, currency: this.currency };
          dataSend.lots = lots;
        } else {
          dataSend.value = {
            amount: this.value,
            currency: this.currency,
          };
          dataSend.auctionPeriod = auctionPeriod;
          dataSend = addTypeSpecificPayload(dataSend, {
            typeAuction,
            minimalStep: this.minimalStep,
            currency: this.currency,
            dutchSteps: this.dutchSteps,
            timeOfOneStep: this.timeOfOneStep,
            antiSnipingEnabled: this.antiSnipingEnabled,
            extensionPeriodMinutes: this.extensionPeriodMinutes,
            reservePriceEnabled: this.reservePriceEnabled,
            reservePrice: this.reservePrice,
            proxyBiddingEnabled: this.proxyBiddingEnabled,
          });
        }
        this.$emit('submit', this.$route.name, dataSend);
      } else {
        this.showSnackbar('Items are required');
      }
    },
    setToday() {
      let today = utils.getProperTime(new Date());
      this.datePicker = today.toISOString().split('T')[0];
    },
    addItem() {
      let newItemCount = this.itemCount + 1;
      this.itemModels[newItemCount] = {};
      this.itemCount = newItemCount;
      this.activeItems += 1;
    },
    removeItem(itemNumber) {
      document.getElementById('item-' + itemNumber.toString()).remove();
      delete this.itemModels[itemNumber];
      this.activeItems -= 1;
    },
    getItemsInfo() {
      return Object.values(this.itemModels);
    },
    getJSONtoSubmit() {
      let jsonToSend = {
        title: this.title,
        description: this.description,
        currency: this.currency,
        value: this.value,
        minimalStep: this.minimalStep,
        items: this.getItemsInfo(),
      };
      jsonToSend = utils.filterEmptyFields(jsonToSend, Object.keys(jsonToSend));
      return jsonToSend;
    },
    formValidation() {
      return true;
    },
  },
  watch: {
    dialog(val) {
      this.errorItems = false;
      val || this.close();
    },
    tenderPeriodEndDateIso() {
      this.tenderPeriodEndDateDisplay = this.formatDate(this.tenderPeriodEndDateIso);
    },
    auctionStartDateIso() {
      this.auctionStartDateDisplay = this.formatDate(this.auctionStartDateIso);
      this.tenderPeriodEndDateDisplay = this.auctionStartDateDisplay;
      this.tenderPeriodEndDateIso = this.auctionStartDateIso;
      if (this.tenderEndTime !== null && this.auctionStartDateDisplay !== null) {
        this.calculateDiffMinutes();
      }
    },
    auctionEndDateIso() {
      this.auctionEndDateDisplay = this.formatDate(this.auctionEndDateIso);
    },
    auctionStartTime(newValue, oldValue) {
      if (
        newValue &&
        typeof newValue === 'string' &&
        newValue.includes(':') &&
        oldValue !== newValue
      ) {
        const parts = newValue.split(':');
        let olddate = new Date(2011, 6, 15, Number(parts[0]), Number(parts[1]), 0, 0);
        let subbed = new Date(olddate - 1000 * 60 * this.diffTenderMinutes);
        this.tenderEndTime =
          subbed.getHours() +
          ':' +
          (subbed.getMinutes() < 10 ? '0' + subbed.getMinutes() : subbed.getMinutes());
      }
      this.calculateDiffMinutes();
    },
  },
  validations: {
    editedItem: {
      documentUrl: {
        url,
      },
      description: {
        required,
      },
      relatedLot: {
        required: requiredIfMultilot,
      },
    },
    choosedAuctionType: {
      required,
    },
    title: {
      required,
    },
    value: {
      required,
    },
    minimalStep: {
      required: requiredIfRoot,
    },
    extensionPeriodMinutes: {
      nonNegativeOptionalMinutes,
    },
    reservePrice: {
      positiveWhenReserveEnabled(value) {
        if (!this.isEnglishReverseContinuousType || !this.reservePriceEnabled) {
          return true;
        }
        if (value === '' || value === null || value === undefined) return false;
        const n = Number(value);
        return Number.isFinite(n) && n > 0;
      },
    },
    dutchSteps: {
      required: requiredDutch,
      minValue: minValue(1),
      maxValue: maxValue(99),
    },
    timeOfOneStep: {
      required: requiredDutch,
      minValue: minValue(0.5),
      maxValue: maxValue(5),
    },
    // dateFormatted: {
    //     required: requiredIf(function () {
    //         return !(this.switcher || !this.choosedquickTimer)
    //     }),
    //     minValue: value => value >= Vue.moment(new Date()).format('DD-MM-YYYY')
    // },
    // tenderEndTime: {
    //     required: requiredIf(function () {
    //         return !(this.switcher || !this.choosedquickTimer)
    //     })
    // },
    // auctionStartDateDisplay: {
    //     required: requiredIf(function () {
    //         return !(this.switcher || !this.choosedquickTimer)
    //     })
    // },
    // auctionStartTime: {
    //     required: requiredIf(function () {
    //         return !(this.switcher || !this.choosedquickTimer)
    //     })
    // }
  },
  components: {
    AuctionFormInfoTooltip,
    AuctionFormInsiderDutch,
    AuctionFormReverseAntiSnipe,
    AuctionFormReservePrice,
    AuctionFormSchedule,
    AuctionFormLots,
  },
};
</script>
<style lang="scss">
.auction-form {
  tr.v-datatable__progress {
    display: block;
  }
  .theme--light.v-datatable .v-datatable__actions {
    background-color: #e8e8e854;
    height: 36px;
  }
  .theme--light.v-table tbody tr:not(:last-child) {
    border-bottom: 1px solid rgba(0, 0, 0, 0.06);
  }
  hr.mx-2.v-divider.v-divider--inset.v-divider--vertical.theme--light {
    display: none;
  }
  .theme--light.v-text-field > .v-input__control > .v-input__slot:before {
    display: none;
  }
  .v-toolbar__content {
    background: #ebebeb;
  }
}

.textarea.v-text-field > .v-input__control > .v-input__slot:after {
  border-style: none;
}

.custom-input.currencies .v-input__slot {
  background: transparent !important;
  margin: 0;
}

.back-icon {
  font-size: 30px;
  position: absolute;
  right: 0;
  &:hover {
    opacity: 0.6;
  }
}

.layout.form-block.column {
  margin-bottom: 40px;
}
#edit-items:hover,
#add-items:hover {
  background: #009688 !important;
  color: white !important;
}
label[for='auc-type'],
label[for='timer-selector'],
label[for='end-timer-selector'] {
  font-size: 15px;
}
.switcher-section {
  padding-top: 10px;
  margin-bottom: -20px;
  background: #ffffff;
}
.switch-btn {
  width: 25%;
}
.form-block {
  background: #ffffff;
  padding: 44px 35px;
}
.form-section-gap {
  margin-top: 18px;
}

.form-field {
  margin-bottom: 8px;
}

.full-width-field {
  width: 100%;
}

.form-field-message-slot {
  min-height: 20px;
  padding-top: 4px;
}

span.error-input {
  display: block;
  text-align: left;
  color: crimson;
  margin-top: 2px;
  margin-bottom: 2px;
  font-size: 11px;
  line-height: 1.3;
}

.form-items-error {
  margin-top: 6px !important;
}

.new-item-button {
  height: 30px;
}

.dialog-items {
  width: 75%;
}

.header-items {
  justify-content: space-between;
}

.submit-button {
  width: max-content;
  margin-top: 26px;
  padding: 12px 38px;
  color: #ffffff;
  background-color: #2e7f89;
  font-weight: 500;
  text-transform: uppercase;
  font-size: 14px;
  letter-spacing: 0.5px;
}

.submit-button-create {
  margin-top: 40px;
}

.submit-button:hover {
  opacity: 0.9;
}

.material-icons {
  color: #536f51c4;
  cursor: pointer;
}

.remove-item {
  padding-top: 12px;
}

.add-more {
  cursor: pointer;
  span {
    font-weight: 600;
    margin-left: 8px;
  }
}

.auction-form {
  width: 100%;
  
  max-width: 700px;
  margin-right: auto;
  margin-left: auto;
  .auc-type .v-select__selections {
    padding: 6px !important;
  }
  .auction-duration-text {
    color: #216169;
    font-size: 12px;
    font-weight: 500;
  }
  & /deep/ .input-group__messages.input-group__error {
    margin-top: 10px;
  }
  & /deep/ .input-group__selections__comma {
    padding-top: 0;
    color: #216169 !important;
    font-size: 12px;
    font-weight: 500;
    text-transform: uppercase;
  }
  & /deep/ .input-group__input input {
    color: #216169;
    font-size: 12px;
    font-weight: 500;

    &::placeholder {
      color: #216169;
      opacity: 0.46;
    }
  }
}
.custom-group .placeholder {
  white-space: nowrap;
  margin-top: 4px;
  margin-bottom: 8px;
  color: #216169;
  font-size: 12px;
  font-weight: 500;
}

.label-with-tooltip {
  display: flex;
  align-items: center;
  gap: 6px;
}

.item-table {
  span {
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
  padding: 5px 0;
  height: auto;
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;
  background: #e6e6e65e;
  box-shadow: 0 0px 0px 0 rgba(0, 0, 0, 0.2), 0 0px 0px 0 rgba(0, 0, 0, 0.14),
    0 1px 3px 0 rgba(0, 0, 0, 0.12);
}

.items-table .v-datatable__actions {
  flex-wrap: inherit;
}

.card-item {
  height: 26px;
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
  background: #e6e6e6;
  box-shadow: 0 2px 1px -1px rgba(0, 0, 0, 0.2), 0 1px 1px 0 rgba(0, 0, 0, 0.14),
    0 1px 3px 0 rgba(0, 0, 0, 0.12);
}

.custom-input.currencies {
  padding: 7px 10px 8px 16px;
}

.custom-input.currencies .v-input__slot {
  width: 100%;
  padding: 8px;
  background: #ffffffa3;
  height: 36px;
}

.custom-input.textarea {
  max-height: 45vh;
  overflow: auto;
}

.item-count {
  font-size: 11px;
}

.dutch-slider .v-input__icon {
  cursor: pointer;
  border-radius: 20px;
  background: rgb(235, 235, 235);
}

.auc-logo {
  width: 100%;
  cursor: pointer;
  color: rgba(47, 102, 109, 0.67);
  font-size: 19px;
  font-weight: 700;
  text-transform: uppercase;
  height: 150px;
  background-color: #ffffff;
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
  position: relative;

  .tooltip {
    position: initial;
  }
  i {
    position: absolute;
    right: 0;
    top: 0;
  }
}

.auction-form {
  .auc-logo-block {
    border: 2px solid #2e7f89;
    width: 100%;
  }

  .avatar-custom {
    background-color: #ffffff !important;
    background-size: contain !important;
    background-position: center center !important;
    background-repeat: no-repeat !important;
    width: 100% !important;
  }
}

.items {
  width: auto;
  min-width: 50%;
}

.custom-input {
  font-weight: 500;
  width: 100%;
  height: 46px;
  color: #216169;
  background-color: #c4c4c44d;
  font-size: 12px;
  margin-bottom: 8px;
  padding: 10px 10px 10px 16px;
  &:focus,
  &.textarea:focus-within {
    outline: 2px solid #216169;
  }
  &::placeholder {
    color: #216169;
    font-size: 12px;
    font-weight: 500;
    opacity: 0.46;
  }
}

.datetime-picker {
  margin-bottom: 20px;
}

@media (min-width: 1200px) {
  .dialog-items {
    width: 50%;
  }
}

.proxy-bidding-toggle {
  margin-top: 6px;
}

.proxy-bidding-toggle-inner {
  display: flex;
  align-items: center;
  gap: 8px;

  input,
  label {
    cursor: pointer;
  }

  label {
    color: #216169;
    font-size: 12px;
    font-weight: 500;
  }
}

.multilot-total-readout {
  width: 100%;
  font-weight: 500;
  color: #216169;
  background-color: #c4c4c44d;
  font-size: 12px;
  height: 46px;
  line-height: 26px;
  margin-bottom: 8px;
  padding: 10px 10px 10px 16px;
  box-sizing: border-box;
}

.multilot-total-hint {
  font-size: 11px;
  font-weight: 500;
  color: #216169;
  opacity: 0.7;
}
</style>
